import { cn } from '../../lib/cn'

interface LogoProps {
  /** Ring diameter in px (sidebar 38, signup 40). */
  size?: number
  /** Show the wordmark + tagline beside the mark. */
  showText?: boolean
  /** Surface colour behind the ring, used for the inset shadow. */
  insetColor?: string
  className?: string
}

/**
 * The ring-and-dot brand mark with wordmark + mono tagline.
 *
 * Two spacing details that are easy to lose in a refactor:
 *
 * 1. The tagline is the width driver, not the wordmark — at 9.5px tracked it measures ~166px
 *    against the wordmark's ~136px. The lockup therefore needs its own trailing padding, or it
 *    ends flush on the tagline's last glyph and crowds whatever sits to its right.
 * 2. `letter-spacing` is applied *after* every character including the last, so a tracked line
 *    carries a phantom gap on its right edge. `-0.1em` of negative right margin cancels exactly
 *    that one trailing unit, so the tagline's optical edge is its real edge and the two lines
 *    align honestly against a shared right boundary.
 */
export function Logo({ size = 38, showText = true, insetColor = '#f7f1e6', className }: LogoProps) {
  const dot = Math.round(size * 0.4)
  return (
    <div className={cn('flex items-center gap-3.5 pr-2', className)}>
      <span
        className="flex flex-none items-center justify-center rounded-full border-[3px] border-ink"
        style={{ width: size, height: size, boxShadow: `inset 0 0 0 3px ${insetColor}` }}
      >
        <span className="rounded-full bg-terra" style={{ width: dot, height: dot }} />
      </span>
      {showText && (
        <span className="min-w-0">
          <span className="block truncate font-serif text-[23px] font-semibold leading-none tracking-[-.02em] text-ink">
            Circle of Trust
          </span>
          {/* `truncate` rather than a bare `whitespace-nowrap`: the parent is `min-w-0` inside a
              flex row, so in a narrow rail this line would otherwise overflow its column and run
              under whatever follows instead of clipping. */}
          <span className="mt-[6px] -mr-[.1em] block truncate font-mono text-[9.5px] uppercase tracking-[.1em] text-ink-dim">
            For the people you trust.
          </span>
        </span>
      )}
    </div>
  )
}
